import { useInfiniteProducts } from '~/hooks/use-infinite-products';
import { InfiniteProductLoader } from '~/components/infinite-product-loader';
import { Fragment } from 'react';
import { RewardsClubInsertion } from '~/components/rewards-club-card';
/**
 * 🌟 UNIVERSAL PRODUCT GRID COMPONENT
 *
 * This component consolidates all product grid functionality across the site.
 * Instead of having separate CategoryProductGrid, BrandProductGrid, DealsProductGrid, etc.,
 * we use one configurable component that adapts to different page types.
 *
 * 🔧 KEY FEATURES:
 * - Configurable page titles and sort options
 * - Consistent 3-column grid layout (18 products per page)
 * - Mobile-first responsive design
 * - Load more functionality with pagination
 * - Sort dropdown with consistent styling
 * - Universal product card integration
 *
 * 📱 MOBILE UX:
 * - Mobile filter toggle button
 * - Responsive grid (1 col mobile, 2 col tablet, 3 col desktop)
 * - Touch-friendly sort dropdown
 *
 * 🎛️ CONFIGURATION:
 * - pageType: 'category' | 'brand' | 'deals' | 'shop' | 'homepage'
 * - title: Display title for the grid
 * - showMobileFilterToggle: Whether to show mobile filter button
 * - onMobileFilterToggle: Handler for mobile filter button
 *
 * 🔗 RELATED FILES:
 * - ~/components/universal-product-card.tsx - Individual product display
 * - ~/context/product-context.tsx - Product state management
 * - ~/components/universal-filter-menu.tsx - Filter menu system
 *
 * 🚀 USAGE EXAMPLES:
 * ```tsx
 * // Category Page
 * <UniversalProductGrid
 *   config={{ pageType: 'category', title: 'Shop All Flower Products' }}
 *   onMobileFilterToggle={toggleMobileFilter}
 *   total={total}
 * />
 *
 * // Brand Page
 * <UniversalProductGrid
 *   config={{ pageType: 'brand', title: 'Shop All PlugPlay Products' }}
 *   onMobileFilterToggle={toggleMobileFilter}
 *   total={total}
 * />
 *
 * // Deals Page
 * <UniversalProductGrid
 *   config={{ pageType: 'deals', title: 'Cannabis Deals & Discounts' }}
 *   onMobileFilterToggle={toggleMobileFilter}
 *   total={total}
 * />
 * ```
 */

import React, { useState, useEffect } from 'react';
import { useProductContext } from '~/context/product-context';
import { useReviewCache } from '~/context/review-context';
import UniversalProductCard from '~/components/universal-product-card';
import { useLocation, useSearchParams } from '@remix-run/react';
import NoProductsFound from './no-products-found';
import BrandLogo from './brand-logo';
import { useStore } from '~/context/store-context';
import { useStores } from '~/hooks/useStores';

// Configuration interface for different page types
export interface UniversalProductGridConfig {
  pageType: 'category' | 'brand' | 'deals' | 'shop' | 'homepage';
  title: string;
  brandName?: string;
  showMobileFilterToggle?: boolean;
}

interface UniversalProductGridProps {
  config: UniversalProductGridConfig;
  onMobileFilterToggle: () => void;
  total: number;
  // Direct product data (like Embarc) - bypasses ProductProvider
  products?: Array<Record<string, unknown>>;
  isLoading?: boolean;
}

export default function UniversalProductGrid({
  config,
  onMobileFilterToggle,
  total,
  products,
  isLoading: externalIsLoading,
}: UniversalProductGridProps) {
  const location = useLocation();
  const { selectedStore } = useStore();
  const stores = useStores();
  const needsStore = !stores.some((store) => store.value === selectedStore && !store.comingSoon);
  const [searchParams, setSearchParams] = useSearchParams();

  // Use direct products if provided (Embarc pattern), otherwise fall back to ProductProvider
  const productContext = products ? null : useProductContext();
  const allProducts = products || productContext?.allProducts || [];
  const isLoading =
    externalIsLoading !== undefined ? externalIsLoading : productContext?.isLoading || false;
  const infinite = useInfiniteProducts(allProducts, total);
  const displayProducts = infinite.items;
  const { fetchReviewsBatch } = useReviewCache();

  // Track if component is mounted to avoid hydration issues
  const [isMounted, setIsMounted] = useState(false);

  // Mark as mounted after hydration
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Batch fetch reviews for all products when they load (only after hydration with delay)
  useEffect(() => {
    if (isMounted && displayProducts.length > 0) {
      const productIds = displayProducts.map((p) => p.id).filter(Boolean);
      if (productIds.length > 0) {
        // Small delay to ensure hydration completes
        const timeoutId = setTimeout(() => {
          fetchReviewsBatch(productIds);
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [isMounted, displayProducts, fetchReviewsBatch]);

  // Helper functions for NoProductsFound component
  const getCurrentFilters = (): Record<string, string> => {
    const filters: Record<string, string> = {};
    const filterParams = [
      'brand',
      'category',
      'subtype',
      'classification',
      'size',
      'discount_applied',
    ];

    filterParams.forEach((param) => {
      const value = searchParams.get(param);
      if (value) {
        filters[param] = value;
      }
    });

    return filters;
  };

  const getBaseFacetsFromContext = () => {
    // This would need to be passed down from the loader data
    // For now, return undefined and the component will handle it gracefully
    return undefined;
  };

  const handleClearAllFilters = () => {
    const newSearchParams = new URLSearchParams();
    // Preserve non-filter params like sort, search, page
    const preserveParams = ['sort', 'search', 'page'];
    preserveParams.forEach((param) => {
      const value = searchParams.get(param);
      if (value) {
        newSearchParams.set(param, value);
      }
    });
    setSearchParams(newSearchParams, { replace: true });
  };

  const getCategorySlugFromUrl = (): string | undefined => {
    const pathParts = location.pathname.split('/');
    const categoryIndex = pathParts.indexOf('category');
    return categoryIndex !== -1 && pathParts[categoryIndex + 1]
      ? pathParts[categoryIndex + 1]
      : undefined;
  };

  const getBrandSlugFromUrl = (): string | undefined => {
    const pathParts = location.pathname.split('/');
    const brandIndex = pathParts.indexOf('brand');
    return brandIndex !== -1 && pathParts[brandIndex + 1] ? pathParts[brandIndex + 1] : undefined;
  };


  // Local state for sort dropdown
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);

  // Close sort dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('[data-sort-dropdown]')) {
        setIsSortDropdownOpen(false);
      }
    };

    if (isSortDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isSortDropdownOpen]);

  // Sort options (consistent across all pages)
  const sortOptions = [
    'Sort by Popular',
    'Price (Low - High)',
    'Price (High - Low)',
    'Name (A-Z)',
    'Name (Z-A)',
  ];

  const currentSort = searchParams.get('sort') || 'Sort by Popular';

  // Handle sort change
  const handleSortChange = (newSort: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('sort', newSort);
    newParams.set('page', '1'); // Reset to page 1 when sorting
    setSearchParams(newParams);
    setIsSortDropdownOpen(false);
  };

  // Page-specific title formatting
  const getFormattedTitle = () => {
    switch (config.pageType) {
      case 'category':
        return config.title; // e.g., "Shop All Flower Products"
      case 'brand':
        return config.title; // e.g., "Shop All PlugPlay Products"
      case 'deals':
        return config.title; // e.g., "Cannabis Deals & Discounts"
      case 'shop':
        return config.title; // e.g., "Shop All Products"
      case 'homepage':
        return config.title; // e.g., "Featured Products"
      default:
        return config.title;
    }
  };

  if (needsStore)
    return (
      <section
        className="flex min-h-64 flex-col items-center justify-center gap-4 px-6 py-12 text-center"
        aria-label="Select a store"
      >
        <h1 className="text-catalystblue text-2xl font-semibold">Choose a store to shop</h1>
        <p className="max-w-md text-gray-600">
          Select your Catalyst location to see available products, prices and deals.
        </p>
        <button
          type="button"
          onClick={() => window.dispatchEvent(new Event('store:select'))}
          className="bg-catalystblue min-h-11 rounded-md px-6 py-3 font-semibold text-white"
        >
          Select a store
        </button>
      </section>
    );

  return (
    <section className="relative w-full">
      <div className="font-helvetica mx-auto max-w-[108rem] justify-center pb-4 text-lg md:px-2 lg:px-0">
        <div className="mb-4 lg:mb-5 lg:flex lg:items-center lg:justify-between lg:gap-6">
          <div className="flex min-w-0 items-center gap-4 px-4 lg:px-0">
            {config.pageType === 'brand' && config.brandName && (
              <BrandLogo name={config.brandName} prominent />
            )}
            <h1 className="text-2xl font-semibold lg:text-3xl lg:leading-9">
              {getFormattedTitle()}
            </h1>
          </div>
          {/* Desktop Sort Row */}
          <div className="hidden shrink-0 items-center justify-end lg:flex">
            {/* Desktop Sort Dropdown */}
            <div className="relative" data-sort-dropdown>
              <button
                onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
                className="flex items-center gap-3"
                aria-label="Sort options"
                aria-expanded={isSortDropdownOpen}
              >
                <span className="text-base leading-6 font-medium tracking-[0.02em] text-nowrap text-[#264B9A]">
                  {currentSort === 'Sort by Popular' ? 'Popular' : currentSort || 'Popular'}
                </span>
                <svg
                  width="12"
                  height="7"
                  viewBox="0 0 12 7"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`transform transition-transform duration-200 ${isSortDropdownOpen ? 'rotate-180' : ''}`}
                >
                  <path
                    d="M1 1L6 6L11 1"
                    stroke="#264B9A"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {/* Desktop Sort Options */}
              {isSortDropdownOpen && (
                <div className="absolute top-full right-0 z-50 mt-2 min-w-[200px] rounded-lg border border-gray-200 bg-white shadow-lg">
                  <div className="py-2">
                    {sortOptions.map((option) => (
                      <button
                        key={option}
                        onClick={() => handleSortChange(option)}
                        className={`block w-full px-4 py-2 text-left text-sm hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none ${
                          currentSort === option
                            ? 'bg-blue-50 font-medium text-[#264B9A]'
                            : 'text-gray-700'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Filter Toggle & Sort Header */}
        <div className="mb-4 flex items-center justify-between px-4 lg:hidden">
          {/* Mobile Filter Toggle Button - New Enhanced Design */}
          {config.showMobileFilterToggle !== false && (
            <div className="flex items-center gap-2">
              <button
                onClick={onMobileFilterToggle}
                className="flex items-center gap-2"
                aria-label="Open filters"
              >
                {/* Filter Icon */}
                <svg
                  width="16.15"
                  height="14"
                  viewBox="0 0 17 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_293_5943)">
                    <path d="M16.1538 9.69232H0V11.8462H16.1538V9.69232Z" fill="#264B9A" />
                    <path d="M12.9231 7.53845H10.7692V14H12.9231V7.53845Z" fill="#264B9A" />
                    <path d="M16.1538 2.15384H0V4.30769H16.1538V2.15384Z" fill="#264B9A" />
                    <path d="M6.46154 0H4.30769V6.46154H6.46154V0Z" fill="#264B9A" />
                  </g>
                  <defs>
                    <clipPath id="clip0_293_5943">
                      <rect width="16.1538" height="14" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                {/* FILTER Text */}
                <span className="font-helvetica text-base leading-6 font-medium tracking-[0.02em] text-[#264B9A]">
                  FILTER
                </span>
              </button>

              {/* Results Count */}
              <span className="text-sm leading-6 font-light tracking-[0.02em] text-gray-600">
                ({total} results)
              </span>
            </div>
          )}

          {/* Mobile Sort Dropdown */}
          <div className="relative" data-sort-dropdown>
            <button
              onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
              className="flex items-center gap-3"
              aria-label="Sort options"
              aria-expanded={isSortDropdownOpen}
            >
              <span className="text-base leading-6 font-medium tracking-[0.02em] text-nowrap text-[#264B9A]">
                {currentSort === 'Sort by Popular' ? 'Popular' : currentSort || 'Popular'}
              </span>
              <svg
                width="12"
                height="7"
                viewBox="0 0 12 7"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={`transform transition-transform duration-200 ${isSortDropdownOpen ? 'rotate-180' : ''}`}
              >
                <path
                  d="M1 1L6 6L11 1"
                  stroke="#264B9A"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* Mobile Sort Options */}
            {isSortDropdownOpen && (
              <div className="absolute top-full right-0 z-50 mt-2 min-w-[200px] rounded-lg border border-gray-200 bg-white shadow-lg">
                <div className="py-2">
                  {sortOptions.map((option) => (
                    <button
                      key={option}
                      onClick={() => handleSortChange(option)}
                      className={`block w-full px-4 py-2 text-left text-sm hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none ${
                        currentSort === option
                          ? 'bg-blue-50 font-medium text-[#264B9A]'
                          : 'text-gray-700'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Products Grid - Responsive layout: 2 cols mobile, 2 cols medium, 3 cols large desktop */}
        <div className="storefront-product-grid grid grid-cols-2 gap-3 px-2 md:gap-4 lg:gap-5 lg:px-0 xl:grid-cols-3">
          {displayProducts.map((product, index) => (
            <Fragment key={`${product.id}-${index}`}>
              <RewardsClubInsertion
                productIndex={infinite.startIndex + index}
              />
              <UniversalProductCard
                product={product}
                priority={index < 6}
              />
            </Fragment>
          ))}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="mt-8 flex justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#264B9A]"></div>
          </div>
        )}

        <InfiniteProductLoader {...infinite} />

        {/* No Products Found - Enhanced with Smart Suggestions */}
        {!isLoading && displayProducts.length === 0 && (
          <NoProductsFound
            pageType={config.pageType === 'homepage' ? 'shop' : config.pageType}
            filters={getCurrentFilters()}
            baseFacets={getBaseFacetsFromContext()}
            onClearFilters={handleClearAllFilters}
            categorySlug={getCategorySlugFromUrl()}
            brandSlug={getBrandSlugFromUrl()}
          />
        )}

        {/* Results Summary */}
        <div className="mt-6 text-center text-sm text-gray-600">
          Showing {displayProducts.length} of {total} products
        </div>
      </div>
    </section>
  );
}
